// src/components/video/characters.js
//
// THE SBL CAST — student characters for animated lesson videos.
//
// Flat 2D "cut-out" figures drawn on a canvas from jointed parts, so one
// drawing can stand, wave, point, walk, jump, cheer, think, talk and sing.
// Two views: FRONT for everything, SIDE (profile) while walking, so a student
// turns to walk across the screen.
//
// Nothing here touches React, Firebase or the DOM: the builder preview, the
// portal and the MP4 export all call the same drawCharacter() each frame.
//
//   drawCharacter(ctx, "marcus", { x, y, scale, action, t, dir, logo })
//
//   (x, y)  the floor point between the feet
//   t       seconds since this action started (drives the motion)
//   dir     "left" | "right" (walk, point) or "up" (point)
//   logo    an <img> of the SBL logo for the shirt. Load it with
//           crossOrigin="anonymous" (as the sing-along export does) or the
//           canvas can't be recorded. Without one, an "SBL" badge is drawn.
//
// At scale 1 a student is about 300 px tall.
//
// MORE STUDENTS LATER
// -------------------
// CAST entries are plain data (skin, hair style + colours, clothes). Add a
// student by adding an entry, or call registerCharacter(). Students built
// from uploaded drawings will register here too, with the same actions.

/* ------------------------------------------------------------------ */
/* The cast                                                            */
/* ------------------------------------------------------------------ */

const LINE = "#2a2130";

export const CAST = {
  marcus: {
    name: "Marcus",
    blurb: "Fade haircut, blue tee, khaki joggers",
    skin: "#a5653c",
    skinShade: "#87502d",
    skinLight: "#bb7a4d",
    eyes: "#4a2c19",
    lashes: false,
    hair: { style: "fade", color: "#1c1411", shine: "#3a2a22" },
    shirt: "#2266e8",
    shirtShade: "#1a50bb",
    pants: "#d4b27c",
    pantsShade: "#b9965f",
    cargo: true,
    shoes: "#2266e8",
    shoeLight: "#ffffff",
  },
  emma: {
    name: "Emma",
    blurb: "Long wavy hair, purple tee, denim joggers",
    skin: "#f8d7c0",
    skinShade: "#e9b99c",
    skinLight: "#ffe6d6",
    eyes: "#3d6fa8",
    lashes: true,
    hair: { style: "long", color: "#b0702f", shine: "#d8984f", dark: "#8e5822" },
    shirt: "#8a55ec",
    shirtShade: "#6f40cc",
    pants: "#4d78a8",
    pantsShade: "#3d6290",
    shoes: "#ff6fa1",
    shoeLight: "#ffffff",
  },
  lily: {
    name: "Lily",
    blurb: "Bob with bangs, teal tee, plum joggers",
    skin: "#f1cba3",
    skinShade: "#ddae82",
    skinLight: "#fadcbd",
    eyes: "#3b2618",
    lashes: true,
    hair: { style: "bob", color: "#1d1922", shine: "#3d3547", dark: "#110e15", clip: "#ffcf3f" },
    shirt: "#14ad9d",
    shirtShade: "#0e8a7d",
    pants: "#5b4485",
    pantsShade: "#48346c",
    shoes: "#ffcf3f",
    shoeLight: "#ffffff",
  },
  noah: {
    name: "Noah",
    blurb: "Long hair tied back, green tee, brown joggers",
    skin: "#b8794f",
    skinShade: "#9a603b",
    skinLight: "#cb8d62",
    eyes: "#3a2316",
    lashes: false,
    hair: { style: "ponytail", color: "#161214", shine: "#3a3035", dark: "#0b0809", tie: "#e0a63a" },
    shirt: "#2f9a4a",
    shirtShade: "#237a39",
    pants: "#6b5240",
    pantsShade: "#564030",
    cargo: true,
    shoes: "#3b7dd8",
    shoeLight: "#ffffff",
  },
};

export const CAST_ORDER = ["marcus", "emma", "lily", "noah"];

/** Adds (or replaces) a student. Returns its key. */
export const registerCharacter = (key, def) => {
  CAST[key] = def;
  if (!CAST_ORDER.includes(key)) CAST_ORDER.push(key);
  return key;
};

export const ACTIONS = {
  idle: "Stand",
  wave: "Wave",
  point: "Point",
  walk: "Walk",
  jump: "Jump",
  cheer: "Cheer",
  think: "Think",
  talk: "Talk",
  sing: "Sing",
};

/* ------------------------------------------------------------------ */
/* Measurements (scale 1)                                              */
/* ------------------------------------------------------------------ */

const M = {
  hipY: -116,
  hipX: 15,
  thigh: 52,
  shin: 48,
  legW: 27,
  shoulderY: -190,
  shoulderX: 32,
  upperArm: 44,
  foreArm: 41,
  armW: 16,
  hand: 10.5,
  torsoTop: -198,
  torsoW: 64,
  neckTop: -214,
  headY: -268,
  headScale: 1.2, // the head is drawn at 47×50 and scaled up: big, friendly
  headRX: 47,
  headRY: 50,
};

const TAU = Math.PI * 2;
const ease = (x) => x * x * (3 - 2 * x);
const clamp01 = (x) => Math.max(0, Math.min(1, x));
const wave01 = (t, period) => 0.5 - 0.5 * Math.cos((t / period) * TAU);

const blinkAt = (t, seed) => {
  const p = (t + seed * 1.37) % 3.6;
  return p < 0.13 ? Math.abs(p - 0.065) / 0.065 : 1;
};

/* ------------------------------------------------------------------ */
/* Drawing helpers                                                     */
/* ------------------------------------------------------------------ */

const rr = (ctx, x, y, w, h, r) => {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + w, y, x + w, y + h, r);
  ctx.arcTo(x + w, y + h, x, y + h, r);
  ctx.arcTo(x, y + h, x, y, r);
  ctx.arcTo(x, y, x + w, y, r);
  ctx.closePath();
};

/** A tapered capsule from (0,0) straight down `len`, w0 wide at top, w1 at the bottom. */
const capsule = (ctx, len, w0, w1 = w0) => {
  ctx.beginPath();
  ctx.moveTo(-w0 / 2, 0);
  ctx.lineTo(-w1 / 2, len);
  ctx.arc(0, len, w1 / 2, Math.PI, 0, true);
  ctx.lineTo(w0 / 2, 0);
  ctx.arc(0, 0, w0 / 2, 0, Math.PI, true);
  ctx.closePath();
};

/** Fill the current path, add a shade on one side (cel shading), outline it. */
const fillShaded = (ctx, base, shade, side = 1, amount = 0.38) => {
  ctx.fillStyle = base;
  ctx.fill();
  if (shade) {
    ctx.save();
    ctx.clip();
    ctx.fillStyle = shade;
    const big = 400;
    ctx.fillRect(side > 0 ? big * (0.5 - amount) - big / 2 + big * 0.0 : -big, -big, big, big * 2);
    ctx.restore();
  }
  ctx.stroke();
};

/** Shade only the right-hand `frac` of a shape whose bounds are [x0, x1]. */
const shadeRight = (ctx, base, shade, x0, x1, frac = 0.3) => {
  ctx.fillStyle = base;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = shade;
  ctx.fillRect(x1 - (x1 - x0) * frac, -2000, 4000, 4000);
  ctx.restore();
  ctx.stroke();
};

/* ---------- SBL logo on the shirt ---------- */

const drawLogo = (ctx, logo, cx, cy, w) => {
  if (logo && logo.naturalWidth) {
    const h = (w * logo.naturalHeight) / logo.naturalWidth;
    ctx.drawImage(logo, cx - w / 2, cy - h / 2, w, h);
    return;
  }
  // Stand-in until a logo image is supplied.
  ctx.save();
  ctx.fillStyle = "rgba(255,255,255,0.92)";
  rr(ctx, cx - w / 2, cy - w * 0.28, w, w * 0.56, w * 0.14);
  ctx.fill();
  ctx.fillStyle = "#2a2130";
  ctx.font = `900 ${Math.round(w * 0.36)}px "Arial Black", "Trebuchet MS", sans-serif`;
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("SBL", cx, cy + 1);
  ctx.restore();
};

/* ------------------------------------------------------------------ */
/* Poses (front view)                                                  */
/* ------------------------------------------------------------------ */
//
// Arms: [shoulder, elbow] in radians. 0 = hanging down; positive swings OUT
// and UP from the body (π/2 = straight out, π = straight up). Elbow adds to
// that. armL is the arm on the viewer's right, armR on the viewer's left.

const basePose = () => ({
  bodyY: 0,
  headTilt: 0,
  lookX: 0,
  lookY: 0,
  armL: [0.1, 0.06],
  armR: [0.1, 0.06],
  legL: [0, 0],
  legR: [0, 0],
  mouth: 0,
  smile: 1,
  brows: 0,
  eyesHappy: false,
  pointHand: null,
});

export const poseFor = (action, t, opts = {}) => {
  const p = basePose();
  p.bodyY = Math.sin(t * 2.2) * 1.5;
  p.armL[0] += Math.sin(t * 1.6) * 0.03;
  p.armR[0] += Math.sin(t * 1.6 + 1) * 0.03;

  switch (action) {
    case "wave": {
      const s = Math.sin(t * 9);
      p.armL = [1.85, 1.0 + s * 0.42];
      p.headTilt = 0.06;
      p.mouth = 0.35;
      break;
    }
    case "point": {
      const dir = opts.dir || "right";
      const k = ease(clamp01(t / 0.35));
      if (dir === "up") {
        p.armL = [0.1 + k * 2.2, 0.06 + k * 0.5];
        p.lookY = -1;
        p.headTilt = 0.08;
        p.pointHand = "L";
      } else {
        const arm = [0.1 + k * 1.38, 0.06 + k * 0.22];
        if (dir === "right") {
          p.armL = arm;
          p.lookX = 1;
          p.pointHand = "L";
        } else {
          p.armR = arm;
          p.lookX = -1;
          p.pointHand = "R";
        }
      }
      p.brows = 0.3;
      p.mouth = 0.15;
      break;
    }
    case "jump": {
      const cyc = (t % 1.1) / 1.1;
      let h = 0;
      let crouch = 0;
      if (cyc < 0.18) crouch = ease(cyc / 0.18);
      else if (cyc < 0.78) {
        const k = (cyc - 0.18) / 0.6;
        h = 4 * k * (1 - k) * 80;
        crouch = Math.max(0, 1 - k * 4);
      } else crouch = 1 - ease((cyc - 0.78) / 0.22);
      p.bodyY = -h + crouch * 12;
      const up = h / 80;
      p.armL = [0.15 + up * 1.85, 0.2 + up * 0.8];
      p.armR = [0.15 + up * 1.85, 0.2 + up * 0.8];
      p.legL = [crouch * 0.3 + up * 0.22, crouch * 0.6 + up * 0.5];
      p.legR = [-crouch * 0.3 - up * 0.22, crouch * 0.6 + up * 0.5];
      p.mouth = up * 0.85;
      p.eyesHappy = up > 0.4;
      break;
    }
    case "cheer": {
      const b = wave01(t, 0.5);
      p.bodyY = -b * 10;
      p.armL = [1.95 + b * 0.12, 0.85 - b * 0.1];
      p.armR = [1.95 + b * 0.12, 0.85 - b * 0.1];
      p.mouth = 0.8;
      p.eyesHappy = true;
      p.brows = 0.5;
      break;
    }
    case "think": {
      p.armL = [-0.32, 3.62];
      p.armR = [0.22, 0.5];
      p.headTilt = -0.1 + Math.sin(t * 1.4) * 0.03;
      p.lookX = -0.6;
      p.lookY = -1;
      p.smile = 0.15;
      p.brows = 0.6;
      break;
    }
    case "talk":
    case "sing": {
      const sing = action === "sing";
      const syl = Math.abs(Math.sin(t * (sing ? 7 : 11))) * (0.55 + 0.45 * Math.abs(Math.sin(t * 2.3)));
      p.mouth = sing ? 0.35 + syl * 0.6 : syl * 0.75;
      p.headTilt = Math.sin(t * (sing ? 1.8 : 2.6)) * (sing ? 0.08 : 0.04);
      if (sing) {
        p.bodyY += Math.sin(t * 3.6) * 2.5;
        p.armL = [0.5 + Math.sin(t * 1.8) * 0.15, 0.95];
        p.armR = [0.5 - Math.sin(t * 1.8) * 0.15, 0.95];
        p.eyesHappy = Math.sin(t * 0.9) > 0.3;
      } else {
        p.armL = [0.32 + Math.max(0, Math.sin(t * 2.2)) * 0.5, 0.75];
      }
      break;
    }
    default:
      break;
  }
  return p;
};

/* ------------------------------------------------------------------ */
/* FRONT VIEW                                                          */
/* ------------------------------------------------------------------ */

const frontArm = (ctx, c, side, [sh, el], pointing) => {
  ctx.save();
  ctx.translate(side * M.shoulderX, M.shoulderY);
  ctx.rotate(-side * sh);
  // upper arm (skin) then the short sleeve over its top
  capsule(ctx, M.upperArm, M.armW, M.armW - 2);
  fillShaded(ctx, c.skin, null);
  ctx.save();
  ctx.translate(0, M.upperArm);
  ctx.rotate(-side * el);
  capsule(ctx, M.foreArm, M.armW - 2, M.armW - 4);
  ctx.fillStyle = c.skin;
  ctx.fill();
  ctx.stroke();
  // hand
  ctx.translate(0, M.foreArm + 3);
  ctx.beginPath();
  ctx.ellipse(0, 0, M.hand - 1, M.hand + 1, 0, 0, TAU);
  ctx.fillStyle = c.skin;
  ctx.fill();
  ctx.stroke();
  if (pointing) {
    rr(ctx, -3.5, 4, 7, 17, 3.5);
    ctx.fillStyle = c.skin;
    ctx.fill();
    ctx.stroke();
  } else {
    // thumb
    ctx.beginPath();
    ctx.ellipse(side * -7, -2, 3.5, 5.5, side * 0.5, 0, TAU);
    ctx.fillStyle = c.skin;
    ctx.fill();
    ctx.stroke();
  }
  ctx.restore();
  // sleeve
  ctx.beginPath();
  ctx.moveTo(-M.armW / 2 - 3, -6);
  ctx.lineTo(-M.armW / 2 - 6, 24);
  ctx.quadraticCurveTo(0, 29, M.armW / 2 + 6, 24);
  ctx.lineTo(M.armW / 2 + 3, -6);
  ctx.closePath();
  ctx.fillStyle = c.shirt;
  ctx.fill();
  ctx.stroke();
  ctx.beginPath();
  ctx.moveTo(-M.armW / 2 - 5, 19);
  ctx.quadraticCurveTo(0, 23, M.armW / 2 + 5, 19);
  ctx.strokeStyle = c.shirtShade;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 3;
  ctx.restore();
};

const sneaker = (ctx, c, toward = 1, side = false) => {
  // toward: +1 toe points right (side view), 0 = front view
  if (!side) {
    rr(ctx, -15, -9, 30, 18, 8);
    ctx.fillStyle = c.shoes;
    ctx.fill();
    ctx.stroke();
    // white sole + toe cap
    rr(ctx, -16, 4, 32, 8, 4);
    ctx.fillStyle = c.shoeLight;
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(0, -1, 8, 5, 0, Math.PI, 0);
    ctx.fillStyle = c.shoeLight;
    ctx.fill();
    // laces
    ctx.beginPath();
    ctx.moveTo(-5, -6);
    ctx.lineTo(5, -3);
    ctx.moveTo(5, -6);
    ctx.lineTo(-5, -3);
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 1.6;
    ctx.stroke();
    ctx.lineWidth = 3;
    return;
  }
  ctx.save();
  ctx.scale(toward, 1);
  ctx.beginPath();
  ctx.moveTo(-12, -10);
  ctx.quadraticCurveTo(4, -14, 12, -6);
  ctx.quadraticCurveTo(26, -4, 26, 4);
  ctx.lineTo(-14, 4);
  ctx.closePath();
  ctx.fillStyle = c.shoes;
  ctx.fill();
  ctx.stroke();
  rr(ctx, -15, 2, 43, 8, 4);
  ctx.fillStyle = c.shoeLight;
  ctx.fill();
  ctx.stroke();
  // swoosh-free side stripe
  ctx.beginPath();
  ctx.moveTo(-6, -4);
  ctx.quadraticCurveTo(6, -2, 16, -6);
  ctx.strokeStyle = c.shoeLight;
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.strokeStyle = LINE;
  ctx.restore();
};

const frontLeg = (ctx, c, side, [hip, knee]) => {
  ctx.save();
  ctx.translate(side * M.hipX, M.hipY);
  ctx.rotate(-hip * 0.6);
  capsule(ctx, M.thigh, M.legW + 2, M.legW);
  shadeRight(ctx, c.pants, c.pantsShade, -12, 12, side > 0 ? 0.35 : 0.15);
  // cargo pocket on the outer thigh
  if (c.cargo) {
    rr(ctx, side > 0 ? 1 : -11, 16, 10, 16, 2);
    ctx.fillStyle = c.pantsShade;
    ctx.fill();
    ctx.lineWidth = 1.6;
    ctx.stroke();
    ctx.lineWidth = 3;
  }
  ctx.translate(0, M.thigh);
  ctx.rotate(knee * side * 0.25);
  capsule(ctx, M.shin, M.legW, M.legW - 2);
  shadeRight(ctx, c.pants, c.pantsShade, -12, 12, side > 0 ? 0.35 : 0.15);
  // jogger cuff
  rr(ctx, -(M.legW - 1) / 2, M.shin - 6, M.legW - 1, 9, 4);
  ctx.fillStyle = c.pantsShade;
  ctx.fill();
  ctx.stroke();
  ctx.translate(0, M.shin + 13);
  sneaker(ctx, c, 0, false);
  ctx.restore();
};

const frontTorso = (ctx, c, logo) => {
  const top = M.torsoTop;
  const w = M.torsoW;
  const bottom = M.hipY + 14;
  ctx.beginPath();
  ctx.moveTo(-w / 2 + 6, top);
  ctx.quadraticCurveTo(-w / 2 - 2, top + 4, -w / 2 - 1, top + 22);
  ctx.quadraticCurveTo(-w / 2 + 1, top + 55, -w / 2 + 3, bottom);
  ctx.quadraticCurveTo(0, bottom + 5, w / 2 - 3, bottom);
  ctx.quadraticCurveTo(w / 2 - 1, top + 55, w / 2 + 1, top + 22);
  ctx.quadraticCurveTo(w / 2 + 2, top + 4, w / 2 - 6, top);
  ctx.closePath();
  shadeRight(ctx, c.shirt, c.shirtShade, -w / 2, w / 2, 0.22);
  // a soft fold
  ctx.beginPath();
  ctx.moveTo(-w / 2 + 10, bottom - 14);
  ctx.quadraticCurveTo(-6, bottom - 20, 4, bottom - 12);
  ctx.strokeStyle = c.shirtShade;
  ctx.lineWidth = 2;
  ctx.stroke();
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 3;
  // crew neck
  ctx.beginPath();
  ctx.ellipse(0, top + 1, 14, 6, 0, 0, Math.PI);
  ctx.fillStyle = c.shirtShade;
  ctx.fill();
  ctx.stroke();
  // logo
  drawLogo(ctx, logo, 0, top + 36, 34);
};

/* ---------- hair (front) ---------- */

const frontHairBack = (ctx, c, t) => {
  const h = c.hair;
  const RX = M.headRX;
  ctx.fillStyle = h.color;
  if (h.style === "long") {
    const sway = Math.sin(t * 1.7) * 2.5;
    ctx.beginPath();
    ctx.moveTo(-RX - 2, -8);
    ctx.bezierCurveTo(-RX - 14, 40, -RX - 4 + sway, 92, -RX + 12 + sway, 104);
    // wavy ends
    ctx.quadraticCurveTo(-RX + 24 + sway, 96, -RX + 30 + sway, 106);
    ctx.lineTo(RX - 30 + sway, 106);
    ctx.quadraticCurveTo(RX - 24 + sway, 96, RX - 12 + sway, 104);
    ctx.bezierCurveTo(RX + 4 + sway, 92, RX + 14, 40, RX + 2, -8);
    ctx.ellipse(0, -8, RX + 2, M.headRY + 2, 0, 0, Math.PI, true);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = h.dark;
    ctx.lineWidth = 2;
    [-RX + 6, RX - 6].forEach((x) => {
      ctx.beginPath();
      ctx.moveTo(x, 20);
      ctx.quadraticCurveTo(x + (x < 0 ? -6 : 6), 60, x + (x < 0 ? 4 : -4), 96);
      ctx.stroke();
    });
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 3;
  } else if (h.style === "bob") {
    ctx.beginPath();
    ctx.moveTo(-RX - 7, -6);
    ctx.quadraticCurveTo(-RX - 10, 34, -RX + 2, 46);
    ctx.lineTo(RX - 2, 46);
    ctx.quadraticCurveTo(RX + 10, 34, RX + 7, -6);
    ctx.ellipse(0, -6, RX + 7, M.headRY + 6, 0, 0, Math.PI, true);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  } else if (h.style === "ponytail") {
    const sway = Math.sin(t * 2) * 0.06;
    ctx.save();
    ctx.translate(RX * 0.62, M.headRY * 0.3);
    ctx.rotate(-0.45 + sway);
    ctx.beginPath();
    ctx.moveTo(-10, 0);
    ctx.bezierCurveTo(-16, 34, -6, 70, 4, 88);
    ctx.bezierCurveTo(13, 70, 17, 34, 10, 0);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-2, 16);
    ctx.quadraticCurveTo(2, 46, 3, 74);
    ctx.strokeStyle = h.shine;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 3;
    rr(ctx, -11, 4, 22, 8, 3);
    ctx.fillStyle = h.tie;
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
};

const frontHairFront = (ctx, c) => {
  const h = c.hair;
  const RX = M.headRX;
  const RY = M.headRY;
  ctx.fillStyle = h.color;
  if (h.style === "fade") {
    // faded sides: hair colour thinning toward the ears
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(0, 0, RX, RY, 0, 0, TAU);
    ctx.clip();
    // only at the temples, above the ears, so the face stays clear
    const g = ctx.createLinearGradient(0, -RY * 0.55, 0, -RY * 0.08);
    g.addColorStop(0, "rgba(28,20,17,0.8)");
    g.addColorStop(1, "rgba(28,20,17,0)");
    ctx.fillStyle = g;
    ctx.fillRect(-RX, -RY * 0.6, RX * 0.26, RY * 0.55);
    ctx.fillRect(RX * 0.74, -RY * 0.6, RX * 0.26, RY * 0.55);
    ctx.restore();
    // textured top with a crisp line-up at the forehead
    ctx.beginPath();
    ctx.moveTo(-RX + 2, -RY * 0.42);
    ctx.quadraticCurveTo(-RX - 2, -RY - 4, -RX * 0.2, -RY - 10);
    ctx.quadraticCurveTo(RX * 0.5, -RY - 12, RX - 2, -RY * 0.55);
    ctx.lineTo(RX - 6, -RY * 0.42);
    ctx.lineTo(RX * 0.62, -RY * 0.5);
    ctx.quadraticCurveTo(0, -RY * 0.6, -RX * 0.62, -RY * 0.5);
    ctx.closePath();
    ctx.fillStyle = h.color;
    ctx.fill();
    ctx.stroke();
    // curls
    ctx.save();
    ctx.clip();
    ctx.strokeStyle = h.shine;
    ctx.lineWidth = 2;
    for (let row = 0; row < 4; row += 1) {
      for (let k = 0; k < 9; k += 1) {
        const x = -RX * 0.85 + k * RX * 0.21 + (row % 2) * RX * 0.1;
        const y = -RY * 0.62 - row * 9;
        ctx.beginPath();
        ctx.arc(x, y, 3.6, Math.PI * 0.15, Math.PI * 1.25);
        ctx.stroke();
      }
    }
    ctx.restore();
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 3;
  } else if (h.style === "long") {
    // side part, sweeping bangs
    ctx.beginPath();
    ctx.moveTo(-RX - 2, 6);
    ctx.quadraticCurveTo(-RX - 6, -RY - 2, -RX * 0.15, -RY - 6);
    ctx.quadraticCurveTo(RX + 6, -RY + 2, RX + 2, 6);
    ctx.quadraticCurveTo(RX - 6, -RY * 0.3, RX * 0.25, -RY * 0.48);
    ctx.quadraticCurveTo(-RX * 0.2, -RY * 0.22, -RX * 0.6, -RY * 0.3);
    ctx.quadraticCurveTo(-RX * 0.85, -8, -RX - 2, 6);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.moveTo(-RX * 0.15, -RY - 4);
    ctx.quadraticCurveTo(-RX * 0.35, -RY * 0.6, -RX * 0.6, -RY * 0.32);
    ctx.strokeStyle = h.dark;
    ctx.lineWidth = 2;
    ctx.stroke();
    ctx.beginPath();
    ctx.ellipse(RX * 0.32, -RY * 0.78, 13, 4, -0.35, 0, TAU);
    ctx.fillStyle = h.shine;
    ctx.fill();
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 3;
  } else if (h.style === "bob") {
    ctx.beginPath();
    ctx.moveTo(-RX - 5, 8);
    ctx.quadraticCurveTo(-RX - 5, -RY - 4, 0, -RY - 9);
    ctx.quadraticCurveTo(RX + 5, -RY - 4, RX + 5, 8);
    ctx.lineTo(RX - 6, 4);
    ctx.lineTo(RX - 8, -RY * 0.36);
    ctx.lineTo(-RX + 8, -RY * 0.36);
    ctx.lineTo(-RX + 6, 4);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // fringe strands
    ctx.strokeStyle = h.shine;
    ctx.lineWidth = 2;
    [-24, -8, 8, 24].forEach((x) => {
      ctx.beginPath();
      ctx.moveTo(x, -RY * 0.36 - 1);
      ctx.lineTo(x * 0.9, -RY * 0.7);
      ctx.stroke();
    });
    ctx.beginPath();
    ctx.ellipse(-RX * 0.25, -RY * 0.8, 14, 4, 0.25, 0, TAU);
    ctx.fillStyle = h.shine;
    ctx.fill();
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 3;
    ctx.save();
    ctx.translate(RX * 0.58, -RY * 0.62);
    ctx.rotate(-0.5);
    rr(ctx, -11, -4, 22, 8, 4);
    ctx.fillStyle = h.clip;
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  } else if (h.style === "ponytail") {
    ctx.beginPath();
    ctx.moveTo(-RX + 1, -2);
    ctx.quadraticCurveTo(-RX - 4, -RY - 3, 0, -RY - 6);
    ctx.quadraticCurveTo(RX + 4, -RY - 3, RX - 1, -2);
    ctx.quadraticCurveTo(RX - 8, -RY * 0.45, 2, -RY * 0.64);
    ctx.quadraticCurveTo(-RX + 8, -RY * 0.45, -RX + 1, -2);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    ctx.strokeStyle = h.shine;
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.moveTo(2, -RY - 5);
    ctx.quadraticCurveTo(0, -RY * 0.8, 2, -RY * 0.64);
    ctx.stroke();
    [-1, 1].forEach((s) => {
      ctx.beginPath();
      ctx.moveTo(s * 10, -RY - 2);
      ctx.quadraticCurveTo(s * RX * 0.6, -RY * 0.85, s * RX * 0.8, -RY * 0.25);
      ctx.stroke();
    });
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 3;
  }
};

/* ---------- face (front) ---------- */

const frontEye = (ctx, c, x, y, p, blink) => {
  if (p.eyesHappy) {
    ctx.beginPath();
    ctx.arc(x, y + 4, 8, Math.PI * 1.12, Math.PI * 1.88);
    ctx.lineWidth = 3.5;
    ctx.stroke();
    ctx.lineWidth = 3;
    return;
  }
  if (blink < 0.35) {
    ctx.beginPath();
    ctx.moveTo(x - 9, y + 1);
    ctx.quadraticCurveTo(x, y + 5, x + 9, y + 1);
    ctx.stroke();
    return;
  }
  const lx = p.lookX * 2.8;
  const ly = p.lookY * 2.5;
  ctx.save();
  ctx.beginPath();
  ctx.ellipse(x, y, 10, 12 * blink, 0, 0, TAU);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.clip();
  ctx.beginPath();
  ctx.ellipse(x + lx, y + 1.5 + ly, 8.6, 9.6, 0, 0, TAU);
  ctx.fillStyle = c.eyes;
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x + lx, y + 2 + ly, 4.6, 0, TAU);
  ctx.fillStyle = "#120c10";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x + lx + 3, y - 2 + ly, 3, 0, TAU);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
  ctx.beginPath();
  ctx.arc(x + lx - 3, y + 5 + ly, 1.4, 0, TAU);
  ctx.fill();
  ctx.restore();
  // upper lid line (no outline round the lower edge: softer look)
  ctx.beginPath();
  ctx.ellipse(x, y, 10.3, 12.3 * blink, 0, Math.PI * 1.02, Math.PI * 1.98);
  ctx.lineWidth = 3.6;
  ctx.stroke();
  if (c.lashes) {
    const s = x < 0 ? -1 : 1;
    ctx.beginPath();
    ctx.moveTo(x + s * 8.5, y - 6 * blink);
    ctx.lineTo(x + s * 13, y - 10 * blink);
    ctx.lineWidth = 2.5;
    ctx.stroke();
  }
  ctx.lineWidth = 3;
};

const frontFace = (ctx, c, p, blink) => {
  const RX = M.headRX;
  const RY = M.headRY;
  // ears (hidden under long hair / bob)
  if (c.hair.style !== "long" && c.hair.style !== "bob") {
    [-1, 1].forEach((s) => {
      ctx.beginPath();
      ctx.ellipse(s * (RX - 1), 6, 10, 14, 0, 0, TAU);
      ctx.fillStyle = c.skin;
      ctx.fill();
      ctx.stroke();
      ctx.beginPath();
      ctx.arc(s * (RX + 1), 6, 5, s > 0 ? -1.2 : Math.PI - 1.9, s > 0 ? 1.9 : Math.PI + 1.2);
      ctx.strokeStyle = c.skinShade;
      ctx.lineWidth = 2.5;
      ctx.stroke();
      ctx.strokeStyle = LINE;
      ctx.lineWidth = 3;
    });
  }
  // head, slightly wider at the cheeks
  ctx.beginPath();
  ctx.ellipse(0, 0, RX, RY, 0, 0, TAU);
  ctx.fillStyle = c.skin;
  ctx.fill();
  ctx.save();
  ctx.clip();
  ctx.fillStyle = c.skinShade;
  ctx.globalAlpha = 0.28;
  ctx.beginPath();
  ctx.ellipse(RX * 1.05, RY * 0.2, RX * 0.42, RY * 1.1, 0, 0, TAU);
  ctx.fill();
  ctx.globalAlpha = 1;
  ctx.restore();
  ctx.beginPath();
  ctx.ellipse(0, 0, RX, RY, 0, 0, TAU);
  ctx.stroke();
  // cheeks
  ctx.fillStyle = "rgba(255,110,110,0.18)";
  [-1, 1].forEach((s) => {
    ctx.beginPath();
    ctx.ellipse(s * 28, 18, 10, 6.5, 0, 0, TAU);
    ctx.fill();
  });
  // eyes
  frontEye(ctx, c, -18, 0, p, blink);
  frontEye(ctx, c, 18, 0, p, blink);
  // brows
  [-1, 1].forEach((s) => {
    const by = -18 - p.brows * 5;
    ctx.beginPath();
    ctx.moveTo(s * 9, by + 2);
    ctx.quadraticCurveTo(s * 18, by - 4, s * 27, by + 1);
    ctx.lineWidth = 4;
    ctx.strokeStyle = c.hair.style === "long" ? c.hair.dark : LINE;
    ctx.stroke();
  });
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 3;
  // nose
  ctx.beginPath();
  ctx.moveTo(-5, 15);
  ctx.quadraticCurveTo(0, 19, 5, 15);
  ctx.strokeStyle = c.skinShade;
  ctx.lineWidth = 3;
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(0, 12, 5, 4, 0, 0, TAU);
  ctx.fillStyle = c.skinLight;
  ctx.fill();
  ctx.strokeStyle = LINE;
  // mouth
  const my = 29;
  const open = p.mouth;
  if (open < 0.08) {
    const w = 10 + p.smile * 4;
    ctx.beginPath();
    ctx.moveTo(-w, my - 3);
    ctx.quadraticCurveTo(0, my + 6 * p.smile + 2, w, my - 3);
    ctx.lineWidth = 3;
    ctx.stroke();
    // dimples
    ctx.beginPath();
    ctx.arc(-w - 1, my - 4, 2, 0.6, 2.4);
    ctx.arc(w + 1, my - 4, 2, 0.7, 2.5);
    ctx.lineWidth = 1.6;
    ctx.stroke();
    ctx.lineWidth = 3;
  } else {
    const w = 11 + open * 5;
    const h = 5 + open * 13;
    ctx.beginPath();
    ctx.moveTo(-w, my - 3);
    ctx.quadraticCurveTo(0, my - 5, w, my - 3);
    ctx.quadraticCurveTo(w * 0.9, my + h, 0, my + h);
    ctx.quadraticCurveTo(-w * 0.9, my + h, -w, my - 3);
    ctx.closePath();
    ctx.fillStyle = "#5e1a28";
    ctx.fill();
    ctx.save();
    ctx.clip();
    // top teeth
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(-w, my - 6, w * 2, 3 + h * 0.28);
    // tongue
    ctx.beginPath();
    ctx.ellipse(0, my + h, w * 0.62, h * 0.48, 0, 0, TAU);
    ctx.fillStyle = "#f2707f";
    ctx.fill();
    ctx.restore();
    ctx.beginPath();
    ctx.moveTo(-w, my - 3);
    ctx.quadraticCurveTo(0, my - 5, w, my - 3);
    ctx.quadraticCurveTo(w * 0.9, my + h, 0, my + h);
    ctx.quadraticCurveTo(-w * 0.9, my + h, -w, my - 3);
    ctx.closePath();
    ctx.stroke();
  }
};

const drawFront = (ctx, c, p, t, blink, logo) => {
  const toHead = () => {
    ctx.translate(0, M.headY + 36);
    ctx.rotate(p.headTilt);
    ctx.translate(0, -36);
    ctx.scale(M.headScale, M.headScale);
  };
  // hair that falls behind the shoulders
  ctx.save();
  toHead();
  frontHairBack(ctx, c, t);
  ctx.restore();

  frontLeg(ctx, c, -1, p.legR);
  frontLeg(ctx, c, 1, p.legL);
  frontTorso(ctx, c, logo);
  frontArm(ctx, c, -1, p.armR, p.pointHand === "R");

  // neck
  rr(ctx, -10, M.neckTop - 8, 20, 26, 7);
  ctx.fillStyle = c.skinShade;
  ctx.fill();
  ctx.stroke();

  ctx.save();
  toHead();
  frontFace(ctx, c, p, blink);
  frontHairFront(ctx, c);
  ctx.restore();

  frontArm(ctx, c, 1, p.armL, p.pointHand === "L");
};

/* ------------------------------------------------------------------ */
/* SIDE VIEW (profile, facing right; mirrored for left)                */
/* ------------------------------------------------------------------ */

const sideLeg = (ctx, c, hip, knee, far) => {
  ctx.save();
  ctx.translate(0, M.hipY);
  ctx.rotate(-hip); // positive hip = forward (+x)
  capsule(ctx, M.thigh, M.legW + 2, M.legW);
  ctx.fillStyle = far ? c.pantsShade : c.pants;
  ctx.fill();
  ctx.stroke();
  if (c.cargo && !far) {
    rr(ctx, -6, 16, 13, 17, 2);
    ctx.fillStyle = c.pantsShade;
    ctx.fill();
    ctx.lineWidth = 1.6;
    ctx.stroke();
    ctx.lineWidth = 3;
  }
  ctx.translate(0, M.thigh);
  ctx.rotate(knee); // positive knee = shin swings back
  capsule(ctx, M.shin, M.legW, M.legW - 2);
  ctx.fillStyle = far ? c.pantsShade : c.pants;
  ctx.fill();
  ctx.stroke();
  rr(ctx, -(M.legW - 1) / 2, M.shin - 6, M.legW - 1, 9, 4);
  ctx.fillStyle = c.pantsShade;
  ctx.fill();
  ctx.stroke();
  ctx.translate(0, M.shin + 12);
  ctx.rotate(-knee * 0.4 + hip * 0.5); // keep the foot nearly flat
  if (far) ctx.globalAlpha = 0.92;
  sneaker(ctx, c, 1, true);
  ctx.globalAlpha = 1;
  ctx.restore();
};

const sideArm = (ctx, c, swing, bend, far) => {
  ctx.save();
  ctx.translate(2, M.shoulderY + 2);
  ctx.rotate(-swing);
  capsule(ctx, M.upperArm, M.armW, M.armW - 2);
  ctx.fillStyle = far ? c.skinShade : c.skin;
  ctx.fill();
  ctx.stroke();
  ctx.save();
  ctx.translate(0, M.upperArm);
  ctx.rotate(-bend);
  capsule(ctx, M.foreArm, M.armW - 2, M.armW - 4);
  ctx.fillStyle = far ? c.skinShade : c.skin;
  ctx.fill();
  ctx.stroke();
  ctx.translate(0, M.foreArm + 3);
  ctx.beginPath();
  ctx.ellipse(0, 0, M.hand - 1, M.hand + 1, 0, 0, TAU);
  ctx.fill();
  ctx.stroke();
  ctx.restore();
  // sleeve
  ctx.beginPath();
  ctx.moveTo(-M.armW / 2 - 4, -8);
  ctx.lineTo(-M.armW / 2 - 6, 24);
  ctx.quadraticCurveTo(0, 29, M.armW / 2 + 6, 24);
  ctx.lineTo(M.armW / 2 + 4, -8);
  ctx.closePath();
  ctx.fillStyle = far ? c.shirtShade : c.shirt;
  ctx.fill();
  ctx.stroke();
  ctx.restore();
};

const sideTorso = (ctx, c) => {
  const top = M.torsoTop;
  const bottom = M.hipY + 14;
  ctx.beginPath();
  ctx.moveTo(-18, top + 2);
  ctx.quadraticCurveTo(-26, top + 30, -22, bottom);
  ctx.quadraticCurveTo(0, bottom + 4, 22, bottom);
  ctx.quadraticCurveTo(26, top + 40, 18, top + 2);
  ctx.quadraticCurveTo(0, top - 6, -18, top + 2);
  ctx.closePath();
  shadeRight(ctx, c.shirt, c.shirtShade, -24, 24, 0.0001);
  ctx.save();
  ctx.clip();
  ctx.fillStyle = c.shirtShade;
  ctx.fillRect(-30, top - 10, 12, 200);
  ctx.restore();
  ctx.stroke();
};

const sideHead = (ctx, c, p, blink, t) => {
  const RX = M.headRX - 2;
  const RY = M.headRY;
  const h = c.hair;
  // hair behind the head
  ctx.fillStyle = h.color;
  if (h.style === "long") {
    const sway = Math.sin(t * 5) * 3;
    ctx.beginPath();
    ctx.moveTo(-RX * 0.2, -RY - 2);
    ctx.bezierCurveTo(-RX - 18, -RY * 0.6, -RX - 14 + sway, 70, -RX + 8 + sway, 106);
    ctx.lineTo(-6 + sway, 100);
    ctx.quadraticCurveTo(-10, 40, 4, 10);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  } else if (h.style === "bob") {
    ctx.beginPath();
    ctx.moveTo(RX * 0.3, -RY - 6);
    ctx.quadraticCurveTo(-RX - 12, -RY, -RX - 8, 40);
    ctx.lineTo(4, 44);
    ctx.lineTo(8, 0);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
  } else if (h.style === "ponytail") {
    const sway = Math.sin(t * 6) * 0.12;
    ctx.save();
    ctx.translate(-RX + 4, -6);
    ctx.rotate(0.55 + sway);
    ctx.beginPath();
    ctx.moveTo(-9, 0);
    ctx.bezierCurveTo(-15, 30, -6, 64, 2, 82);
    ctx.bezierCurveTo(12, 64, 15, 30, 9, 0);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    rr(ctx, -10, 2, 20, 8, 3);
    ctx.fillStyle = h.tie;
    ctx.fill();
    ctx.stroke();
    ctx.restore();
  }
  // head shape: round skull, face toward +x
  ctx.beginPath();
  ctx.ellipse(0, 0, RX, RY, 0, 0, TAU);
  ctx.fillStyle = c.skin;
  ctx.fill();
  ctx.stroke();
  // nose bump on the front edge
  ctx.beginPath();
  ctx.moveTo(RX - 3, 4);
  ctx.quadraticCurveTo(RX + 9, 12, RX - 2, 18);
  ctx.fillStyle = c.skin;
  ctx.fill();
  ctx.stroke();
  // ear
  if (h.style !== "long" && h.style !== "bob") {
    ctx.beginPath();
    ctx.ellipse(-6, 6, 9, 13, 0, 0, TAU);
    ctx.fillStyle = c.skin;
    ctx.fill();
    ctx.stroke();
    ctx.beginPath();
    ctx.arc(-5, 6, 4.5, -1.2, 1.9);
    ctx.strokeStyle = c.skinShade;
    ctx.lineWidth = 2.5;
    ctx.stroke();
    ctx.strokeStyle = LINE;
    ctx.lineWidth = 3;
  }
  // cheek
  ctx.beginPath();
  ctx.ellipse(RX * 0.55, 18, 9, 6, 0, 0, TAU);
  ctx.fillStyle = "rgba(255,110,110,0.18)";
  ctx.fill();
  // eye
  const ex = RX * 0.55;
  if (blink < 0.35) {
    ctx.beginPath();
    ctx.moveTo(ex - 7, 1);
    ctx.quadraticCurveTo(ex, 5, ex + 6, 1);
    ctx.stroke();
  } else {
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(ex, 0, 7, 11 * blink, 0, 0, TAU);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.clip();
    ctx.beginPath();
    ctx.arc(ex + 2, 1, 6, 0, TAU);
    ctx.fillStyle = c.eyes;
    ctx.fill();
    ctx.beginPath();
    ctx.arc(ex + 3, 1, 3, 0, TAU);
    ctx.fillStyle = "#120c10";
    ctx.fill();
    ctx.beginPath();
    ctx.arc(ex + 4.5, -2, 1.9, 0, TAU);
    ctx.fillStyle = "#ffffff";
    ctx.fill();
    ctx.restore();
    ctx.beginPath();
    ctx.ellipse(ex, 0, 7.5, 11.3 * blink, 0, Math.PI * 1.1, Math.PI * 1.95);
    ctx.lineWidth = 3.5;
    ctx.stroke();
    ctx.lineWidth = 3;
  }
  // brow
  ctx.beginPath();
  ctx.moveTo(ex - 6, -17);
  ctx.quadraticCurveTo(ex + 2, -21, ex + 9, -16);
  ctx.lineWidth = 4;
  ctx.stroke();
  ctx.lineWidth = 3;
  // mouth
  const open = p.mouth;
  if (open < 0.08) {
    ctx.beginPath();
    ctx.moveTo(RX - 18, 28);
    ctx.quadraticCurveTo(RX - 8, 33, RX - 3, 26);
    ctx.stroke();
  } else {
    ctx.beginPath();
    ctx.moveTo(RX - 18, 26);
    ctx.quadraticCurveTo(RX - 6, 25, RX - 2, 25);
    ctx.quadraticCurveTo(RX - 4, 30 + open * 10, RX - 14, 30 + open * 9);
    ctx.closePath();
    ctx.fillStyle = "#5e1a28";
    ctx.fill();
    ctx.stroke();
  }
  // hair on top
  ctx.fillStyle = h.color;
  if (h.style === "fade") {
    ctx.save();
    ctx.beginPath();
    ctx.ellipse(0, 0, RX, RY, 0, 0, TAU);
    ctx.clip();
    const g = ctx.createLinearGradient(0, -RY * 0.5, 0, RY * 0.1);
    g.addColorStop(0, "rgba(28,20,17,0.6)");
    g.addColorStop(1, "rgba(28,20,17,0)");
    ctx.fillStyle = g;
    ctx.fillRect(-RX, -RY * 0.6, RX * 0.95, RY * 0.75);
    ctx.restore();
    ctx.beginPath();
    ctx.moveTo(-RX + 2, -RY * 0.4);
    ctx.quadraticCurveTo(-RX - 4, -RY - 6, RX * 0.1, -RY - 10);
    ctx.quadraticCurveTo(RX * 0.75, -RY - 6, RX * 0.72, -RY * 0.48);
    ctx.quadraticCurveTo(RX * 0.1, -RY * 0.58, -RX + 2, -RY * 0.4);
    ctx.closePath();
    ctx.fillStyle = h.color;
    ctx.fill();
    ctx.stroke();
  } else if (h.style === "long" || h.style === "bob" || h.style === "ponytail") {
    ctx.beginPath();
    ctx.moveTo(-RX - (h.style === "ponytail" ? 0 : 4), 4);
    ctx.quadraticCurveTo(-RX - 6, -RY - 4, RX * 0.2, -RY - 7);
    ctx.quadraticCurveTo(RX + 4, -RY + 2, RX * 0.85, -RY * 0.3);
    if (h.style === "bob") {
      ctx.lineTo(RX * 0.55, -RY * 0.3);
    } else {
      ctx.quadraticCurveTo(RX * 0.4, -RY * 0.6, -RX * 0.1, -RY * 0.55);
    }
    ctx.quadraticCurveTo(-RX * 0.25, -10, -RX * 0.25, 4);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    if (h.style === "bob") {
      ctx.save();
      ctx.translate(RX * 0.1, -RY * 0.62);
      ctx.rotate(-0.4);
      rr(ctx, -10, -4, 20, 8, 4);
      ctx.fillStyle = h.clip;
      ctx.fill();
      ctx.stroke();
      ctx.restore();
    }
  }
};

const drawSide = (ctx, c, t, blink) => {
  // walk cycle in profile
  const ph = t * 6.2;
  const s = Math.sin(ph);
  const bob = -Math.abs(Math.cos(ph)) * 5;
  ctx.translate(0, bob);
  const hipA = s * 0.36;
  const kneeA = (a) => Math.max(0, -Math.sin(a)) * 0.9 + 0.08;
  // far side first (darker), then torso, then near side
  sideArm(ctx, c, s * 0.5, -0.35, true);
  sideLeg(ctx, c, -hipA, kneeA(ph + Math.PI), true);
  sideLeg(ctx, c, hipA, kneeA(ph), false);
  sideTorso(ctx, c);
  // neck
  rr(ctx, -6, M.neckTop - 8, 18, 26, 7);
  ctx.fillStyle = c.skinShade;
  ctx.fill();
  ctx.stroke();
  ctx.save();
  ctx.translate(4, M.headY);
  ctx.rotate(Math.sin(ph * 2) * 0.02);
  ctx.scale(M.headScale, M.headScale);
  sideHead(ctx, c, { mouth: 0.12 }, blink, t);
  ctx.restore();
  sideArm(ctx, c, -s * 0.5, -0.35, false);
};

/* ------------------------------------------------------------------ */
/* Public: draw one character                                          */
/* ------------------------------------------------------------------ */

const TURN = 0.18; // seconds to turn between front and side

export const drawCharacter = (ctx, key, opts = {}) => {
  const c = CAST[key];
  if (!c) return;
  const { x = 0, y = 0, scale = 1, action = "idle", t = 0, dir, shadow = true, logo = null } = opts;
  const seed = Math.max(0, CAST_ORDER.indexOf(key)) + 1;
  const blink = action === "cheer" ? 1 : blinkAt(t, seed);
  const walking = action === "walk";
  const facing = dir === "left" ? -1 : 1;

  ctx.save();
  ctx.translate(x, y);
  ctx.scale(scale, scale);
  ctx.lineJoin = "round";
  ctx.lineCap = "round";
  ctx.strokeStyle = LINE;
  ctx.lineWidth = 3;

  const p = walking ? null : poseFor(action, t + seed * 0.37, { dir });
  if (shadow) {
    const lift = p ? clamp01(-p.bodyY / 80) : 0;
    ctx.beginPath();
    ctx.ellipse(0, 6, 52 * (1 - lift * 0.35), 10 * (1 - lift * 0.35), 0, 0, TAU);
    ctx.fillStyle = "rgba(30,20,40,0.16)";
    ctx.fill();
  }

  // the turn: squeeze to a sliver, switch view, open back out
  let sx = 1;
  let showSide = walking;
  if (walking && t < TURN) {
    const k = t / TURN;
    sx = Math.max(0.12, Math.abs(1 - 2 * k));
    showSide = k >= 0.5;
  }

  if (showSide) {
    ctx.scale(sx * facing, 1);
    drawSide(ctx, c, t, blink);
  } else {
    ctx.scale(sx, 1);
    const pose = p || poseFor("idle", t, {});
    ctx.translate(0, pose.bodyY);
    drawFront(ctx, c, pose, t, blink, logo);
  }
  ctx.restore();
};

export default drawCharacter;
